/**
 * Occurrences d'un même créneau de modèle dans une prévisualisation.
 *
 * Un créneau qui s'étend sur plusieurs jours (`daySpan`, ex. 12:30 du lundi au
 * vendredi) produit une proposition par jour, toutes avec le même `slotId`.
 * Quand l'opérateur choisit un programme sur l'une d'elles, il peut le reporter
 * sur les autres jours du même créneau plutôt que de refaire le choix cinq fois.
 *
 * Seule la tête de chaque jour compte : les propositions suivantes du même
 * créneau et du même jour ne font que remplir la fenêtre derrière elle.
 */
export type SlotOccurrenceProposal = {
  proposalId: string
  slotId: string
  dayOffset: number
  itemType: string
}

export function sameSlotOccurrences<T extends SlotOccurrenceProposal>(
  proposals: readonly T[],
  sourceProposalId: string,
): T[] {
  const source = proposals.find(proposal => proposal.proposalId === sourceProposalId)
  if (!source) return []
  const headByDay = new Map<number, T>()
  for (const proposal of proposals) {
    if (proposal.slotId !== source.slotId) continue
    if (!headByDay.has(proposal.dayOffset)) headByDay.set(proposal.dayOffset, proposal)
  }
  return [...headByDay.values()]
    .filter(proposal => proposal.dayOffset !== source.dayOffset)
    // Un direct n'accepte pas de substitution : il se pose sur sa source.
    .filter(proposal => proposal.itemType === 'PROGRAM')
    .sort((left, right) => left.dayOffset - right.dayOffset)
}

/**
 * Occurrences cochées d'office : celles qui n'ont encore aucun contenu. Une
 * occurrence déjà résolue est proposée décochée, pour ne pas écraser sans le
 * vouloir un épisode que le modèle a bien trouvé.
 */
export function defaultPropagationTargets<T extends SlotOccurrenceProposal>(
  occurrences: readonly T[],
  lacksContent: (proposal: T) => boolean,
): Set<string> {
  return new Set(occurrences.filter(lacksContent).map(proposal => proposal.proposalId))
}
